import React from "react";
import { Alert, Button, Modal, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import ApiService from "../services/ApiService";
import { TemplateFile } from "../models/Templates";
import { formatDate } from "../utils/dateUtils";
import logger from "../utils/logging";
import { isApiError } from "../errors";
import { ERROR_CODE_PDF_CORRUPTED_FORM_FIELDS } from "../errors/backendErrorCodes";

type TemplatePreviewDialogProps = {
    show: boolean;
    template: TemplateFile | null;
    apiService: ApiService;
    onClose: () => void;
};

const TemplatePreviewDialog: React.FC<TemplatePreviewDialogProps> = ({ show, template, apiService, onClose }) => {
    const { t } = useTranslation(["pdfPreview", "templates"]);
    const [isLoading, setIsLoading] = React.useState(false);
    const [error, setError] = React.useState<Error | null>(null);
    const [blobUrl, setBlobUrl] = React.useState<string | null>(null);
    const blobUrlRef = React.useRef<string | null>(null);

    const updateBlobUrl = React.useCallback((url: string | null) => {
        if (blobUrlRef.current) {
            URL.revokeObjectURL(blobUrlRef.current);
        }
        blobUrlRef.current = url;
        setBlobUrl(url);
    }, []);

    React.useEffect(() => {
        if (!show || !template) {
            setIsLoading(false);
            setError(null);
            updateBlobUrl(null);
            return;
        }

        let cancelled = false;

        const loadPreview = async () => {
            setIsLoading(true);
            setError(null);

            try {
                const blob = await apiService.getTemplatePdf(template.id);
                if (cancelled) {
                    return;
                }
                updateBlobUrl(URL.createObjectURL(blob));
            } catch (err) {
                if (cancelled) {
                    return;
                }
                const fallbackMessage = t("errors.unknown");
                const normalized = err instanceof Error ? err : new Error(fallbackMessage);
                logger.error("Failed to load template PDF preview", normalized);
                updateBlobUrl(null);
                setError(new Error(t("errors.load"), { cause: normalized }));
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        };

        void loadPreview();

        return () => {
            cancelled = true;
            updateBlobUrl(null);
        };
    }, [show, template, apiService, updateBlobUrl, t]);

    React.useEffect(() => {
        return () => {
            updateBlobUrl(null);
        };
    }, [updateBlobUrl]);

    const getErrorMessage = (error: Error): String => {
        if (isApiError(error.cause) && error.cause.errorCode === ERROR_CODE_PDF_CORRUPTED_FORM_FIELDS) {
            return t("templates:upload.modal.upload.corruptedFormFields.message");
        }
        return error.message;
    };

    const bodyContent = () => {
        if (isLoading) {
            return (
                <div className="d-flex align-items-center gap-3">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">{t("loading")}</span>
                    </Spinner>
                    <span>{t("loading")}</span>
                </div>
            );
        }

        if (error && !blobUrl) {
            return (
                <>
                    <Alert variant="danger">
                        <Alert.Heading>{t("errors.heading")}</Alert.Heading>
                        <p className="mb-0">{getErrorMessage(error)}</p>
                    </Alert>
                </>
            );
        }

        if (!blobUrl) {
            return null;
        }

        return (
            <div className="d-flex flex-column gap-3">
                {error && (
                    <Alert variant="warning" className="mb-0">
                        {error.message}
                    </Alert>
                )}
                {template && <p className="mb-0">{t("lastUpdated", { date: formatDate(template.updatedAt) })}</p>}
                <Button
                    as="a"
                    href={blobUrl ?? undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    role="button"
                    disabled={!blobUrl}
                >
                    {t("actions.open")}
                </Button>
            </div>
        );
    };

    return (
        <Modal show={show} onHide={onClose} size="lg" centered backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>{t("title")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {template && (
                    <p className="mb-3">
                        <strong>{template.name}</strong>
                    </p>
                )}
                {bodyContent()}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose}>
                    {t("actions.close")}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default TemplatePreviewDialog;
